/**
 * Elects one AudioPlayer instance to own the media element.
 *
 * The playback state - which file, which blob, playing or not - lives in redux,
 * but every mounted AudioPlayer renders its own ReactPlayer bound to that state.
 * Screens that keep more than one alive therefore fed a single blob to two media
 * elements and played it twice: the auto attendant details screen keeps the
 * hidden player of the call settings tab mounted (tab panels are only hidden,
 * never unmounted) while the call flow tab opens the menu prompt dialog with a
 * player of its own.
 *
 * The oldest mounted instance holds the role, so a dialog opening on top of a
 * screen never takes playback over from it, and unmounting the host passes the
 * role on to the next instance in line.
 */
type PlaybackHostListener = (isHost: boolean) => void;

const listeners: PlaybackHostListener[] = [];

const notifyListeners = () => {
    listeners.forEach((listener, index) => listener(index === 0));
};

/**
 * Registers an instance and returns the function releasing it - hand it back
 * from the effect that claimed the role.
 */
export const claimPlaybackHost = (onHostChange: PlaybackHostListener) => {
    listeners.push(onHostChange);
    notifyListeners();

    return () => {
        const index = listeners.indexOf(onHostChange);

        if (index === -1) {
            return;
        }

        listeners.splice(index, 1);
        notifyListeners();
    };
};